/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { useLazyQuery, useMutation } from '@apollo/client'
import { SIGNIN } from '../../../graphql/mutation'
import { GET_ASSIGNED_TEAM_BY_USER_ID } from '../../../graphql/query'
import { AUTH_TOKEN } from '../../../constants'
import { toast } from 'react-toastify'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required')
})

const initialValues = {
  email: '',
  password: ''
}

const ErrorMessage = ({ touched, error }: { touched?: boolean; error?: string }) => {
  if (touched && error) {
    return (
      <div className='fv-plugins-message-container'>
        <span role='alert'>{error}</span>
      </div>
    )
  }
  return null
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [signin] = useMutation(SIGNIN)
  const [getAssignedTeamByUserId] = useLazyQuery(GET_ASSIGNED_TEAM_BY_USER_ID)
  const [isTeamSelectionVisible, setIsTeamSelectionVisible] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState('')
  const [authToken, setAuthToken] = useState('')
  const [teams, setTeams] = useState([])

  const allowedRoles = [3, 5, 7, 10, 11, 12]
  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true)
      try {
        const res = await signin({
          variables: {
            input: {
              email: values.email,
              password: values.password
            }
          }
        })

        setLoading(false)
        if (res.data.signIn.status === 200) {
          setAuthToken(res.data.signIn.data.token)
          const { data } = await getAssignedTeamByUserId({
            variables: { input: { userId: Number(res.data.signIn.data.user.id) } },
            context: { headers: { Authorization: `Bearer ${res.data.signIn.data.token}` } }
          })
          const formattedTeams = data.getAssignedTeamByUserId.map((team: any) => ({
            id: team.Team.id,
            name: team.Team.name
          }))

          setTeams(formattedTeams)
          if (allowedRoles.includes(res.data.signIn.data.user.UserRole[0].roleId)) {
            setIsTeamSelectionVisible(true)
          } else if (res.data.signIn.data.user.UserRole[0].roleId === 1) {
            const defaultTeam = formattedTeams.find((team: any) => team.name === 'Default Team')
            localStorage.setItem('selectedTeam', defaultTeam.id)
            localStorage.setItem(AUTH_TOKEN, res.data.signIn.data.token)
            document.location.reload()
          } else {
            localStorage.setItem(AUTH_TOKEN, res.data.signIn.data.token)
            document.location.reload()
          }
        } else {
          toast.error(res.data.signIn.message)
        }
      } catch (error) {
        setLoading(false)
        console.error(error)
      } finally {
        setSubmitting(false)
      }
    }
  })

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const handleTeamSubmit = () => {
    if (selectedTeam) {
      localStorage.setItem('selectedTeam', selectedTeam)
      localStorage.setItem(AUTH_TOKEN, authToken)
      document.location.reload()
    } else {
      toast.error('Please select a team')
    }
  }

  const handleTeamSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTeam(e.target.value)
  }

  return (
    <>
      {isTeamSelectionVisible ? (
        <>
          <div className='form w-100'>
            <div className='text-center mb-10'>
              <h1 className='text-dark mb-3'>Select a Team</h1>
            </div>

            <div className='fv-row mb-10'>
              <label className='form-label fs-6 fw-bolder text-dark'>Team</label>
              <select
                className='form-control form-control-lg form-control-solid'
                value={selectedTeam}
                onChange={handleTeamSelection}
              >
                <option value=''>Select team</option>
                {teams.map((team: any) => (
                  <option key={team?.id} value={team?.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            <div className='text-center'>
              <button className='btn btn-lg btn-primary w-100 mb-5' onClick={handleTeamSubmit}>
                Continue
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <form
            className='form w-100'
            onSubmit={formik.handleSubmit}
            noValidate
            id='kt_login_signin_form'
          >
            <div className='text-center mb-10'>
              <h1 className='text-dark mb-3'>Sign In to Botgo</h1>
              <div className='text-gray-400 fw-bold fs-4'>
                New Here?{' '}
                <Link to='/auth/registration' className='link-primary fw-bolder'>
                  Create an Account
                </Link>
              </div>
            </div>

            {formik.status && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}

            <div className='fv-row mb-10'>
              <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
              <input
                placeholder='Enter your email'
                {...formik.getFieldProps('email')}
                className={clsx('form-control form-control-lg form-control-solid', {
                  'is-invalid': formik.touched.email && formik.errors.email,
                  'is-valid': formik.touched.email && !formik.errors.email
                })}
                type='email'
                name='email'
                autoComplete='off'
              />
              <ErrorMessage touched={formik.touched.email} error={formik.errors.email} />
            </div>

            <div className='fv-row mb-10'>
              <div className='d-flex justify-content-between mt-n5'>
                <div className='d-flex flex-stack mb-2'>
                  <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
                </div>
              </div>
              <div className='input-group'>
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete='off'
                  placeholder='Enter your password'
                  {...formik.getFieldProps('password')}
                  className={clsx('form-control form-control-lg form-control-solid', {
                    'is-invalid': formik.touched.password && formik.errors.password,
                    'is-valid': formik.touched.password && !formik.errors.password
                  })}
                />
                <div className='input-group-append'>
                  <span
                    className='form-control form-control-lg form-control-solid'
                    id='togglePassword'
                    onClick={togglePasswordVisibility}
                    style={{
                      cursor: 'pointer',
                      borderRadius: '0px 5px 5px 0px' // Adjust the border radius as needed
                    }}
                  >
                    {showPassword ? (
                      <i className='bi bi-eye-slash text-dark'></i>
                    ) : (
                      <i className='bi bi-eye text-dark'></i>
                    )}
                  </span>
                </div>
              </div>
              <ErrorMessage touched={formik.touched.password} error={formik.errors.password} />
              <Link
                to='/auth/forgot-password'
                className='link-primary fs-6 fw-bolder'
                style={{ marginLeft: '5px' }}
              >
                Forgot Password ?
              </Link>
            </div>

            <div className='text-center'>
              <button
                type='submit'
                id='kt_sign_in_submit'
                className='btn btn-lg btn-primary w-100 mb-5'
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {!loading && <span className='indicator-label'>Continue</span>}
                {loading && (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </>
      )}
    </>
  )
}
